/**
 * Keyboard decisions of a tab list (WAI-ARIA tabs pattern), kept pure so they can be unit-tested
 * in the node env — the same shape as `dialogKeys.ts`. `Tabs.tsx` wires them to real DOM events.
 *
 * Roving focus: one tab is in the Tab order (the selected one), the arrows move between tabs and
 * select as they go, Home/End jump to the ends. Enter and Space need no decision here — the tabs
 * are buttons, and a button activates on both by itself.
 *
 * Both decisions start from the same **stop index**: the selected tab's position, or the first tab
 * when the selected one is not in the list — a guarantee of this helper, so a screen that builds
 * its tab list from data may drop the tab that was open without the next arrow press being a dead
 * one.
 */

const stopIndex = <T>(tabs: readonly T[], active: T): number => Math.max(0, tabs.indexOf(active));

/** The one tab that sits in the page's Tab order — the selected one, or the first while none is. */
export const tabStop = <T>(tabs: readonly T[], active: T): T | undefined => tabs[stopIndex(tabs, active)];

/**
 * The tab an arrow key selects, or `null` when the key is not a tab-list key. Left/Right wrap
 * around the ends; Home/End go to them.
 */
export const tabAfterKey = <T>(key: string, tabs: readonly T[], active: T): T | null => {
  const count = tabs.length;

  if (count === 0) {
    return null;
  }

  const index = stopIndex(tabs, active);

  switch (key) {
    case 'ArrowRight':
      return tabs[(index + 1) % count];
    case 'ArrowLeft':
      return tabs[(index - 1 + count) % count];
    case 'Home':
      return tabs[0];
    case 'End':
      return tabs[count - 1];
    default:
      return null;
  }
};
